import { authenticatedApiRequest, cleanEmptyFields } from "./core";
import type { PaginatedResponse } from "./core";
import type { CreatePayee, Payee, PayeeDetails, UpdatePayee } from "@/types";

export interface PayeeFilters {
  label_id?: string;
  name?: string;
  is_active?: boolean;
  country?: string;
  city?: string;
  party_id?: string;
  page?: number;
  page_size?: number;
  q?: string;
  ordering?: string;
}

export interface PaginatedPayeesResponse extends PaginatedResponse {
  results: Payee[];
}

const basePath = "/contracts/payees";

const toQueryParams = (filters?: PayeeFilters): string => {
  if (!filters) return "";

  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean") {
      params.append(key, value ? "true" : "false");
    } else {
      params.append(key, String(value));
    }
  });
  return params.toString();
};

export const payeesApi = {
  getPayees: async (filters?: PayeeFilters): Promise<PaginatedPayeesResponse> => {
    const queryString = toQueryParams(filters);
    const url = `${basePath}${queryString ? `?${queryString}` : ""}`;
    return authenticatedApiRequest<PaginatedPayeesResponse>(url);
  },
  getPayee: async (id: string): Promise<PayeeDetails> => {
    return authenticatedApiRequest<PayeeDetails>(`${basePath}/${id}`);
  },
  createPayee: async (payee: CreatePayee & { label: string }): Promise<Payee> => {
    return authenticatedApiRequest<Payee>(basePath, {
      method: "POST",
      body: JSON.stringify(cleanEmptyFields(payee)),
    });
  },
  updatePayee: async (id: string, payee: CreatePayee & { label: string }): Promise<Payee> => {
    return authenticatedApiRequest<Payee>(`${basePath}/${id}`, {
      method: "PUT",
      body: JSON.stringify(cleanEmptyFields(payee)),
    });
  },
  patchPayee: async (id: string, updates: Partial<UpdatePayee>): Promise<Payee> => {
    return authenticatedApiRequest<Payee>(`${basePath}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },
  deletePayee: async (id: string): Promise<void> => {
    await authenticatedApiRequest<void>(`${basePath}/${id}`, {
      method: "DELETE",
    });
  },
};
